import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGFM from "remark-gfm";
import { cn } from "../lib/utils";
import { H1 } from "./h1";
import { H2 } from "./h2";
import { H3 } from "./h3";
import { H4 } from "./h4";
import { Link } from "./link";
import { P } from "./p";

interface ContentfulTextProps {
  content: string;
  className?: string;
  ulClassName?: string;
  aClassName?: string;
  pClassName?: string;
  /** Bullet style for unordered lists. "check" renders a check mark per item. */
  listMarker?: "disc" | "check";
}

/**
 * Contentful can have rich text in a string. Style it here like RichText
 */
const ContentfulText = forwardRef<HTMLDivElement, ContentfulTextProps>(
  (
    { content, className, ulClassName, pClassName, listMarker = "disc" },
    ref,
  ) => {
    return (
      <div ref={ref} className={cn(className)}>
        <ReactMarkdown
          remarkPlugins={[remarkGFM]}
          components={{
            p: ({ children }) => (
              <P className={cn("mb-4", pClassName)}>{children}</P>
            ),
            h1: ({ children }) => <H1>{children}</H1>,
            h2: ({ children }) => <H2>{children}</H2>,
            h3: ({ children }) => <H3>{children}</H3>,
            h4: ({ children }) => <H4>{children}</H4>,
            a: ({ href, children }) => (
              <Link className="underline" href={href}>
                {children}
              </Link>
            ),
            ul: ({ children }) => (
              <ul
                className={cn(
                  listMarker === "check" ? "flex flex-col" : "ml-4 list-disc",
                  ulClassName,
                )}
              >
                {children}
              </ul>
            ),
            ...(listMarker === "check" && {
              li: ({ children }: { children?: React.ReactNode }) => (
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="mt-1 h-3.5 w-3.5 shrink-0"
                    aria-hidden
                  />
                  <span>{children}</span>
                </li>
              ),
            }),
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  },
);

ContentfulText.displayName = "ContentfulText";

export { ContentfulText };
